'use client'

import { IconType } from 'react-icons'

import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Text,
  Tooltip,
} from '@chakra-ui/react'

import { Colors } from '@/styles/colors'

export const ActionBar = ({
  label,
  primaryButton,
  extraButton,
  secondaryButton,
}: ActionBarProps) => {
  const bgColor = Colors.hiveWatchDarkBlue
  const borderColor = Colors.hiveWatchDarkBlue
  const dividerColor = Colors.gray300
  const textLabelColor = Colors.white
  const buttonSize = 'sm'

  return (
    <Box
      bottom={6}
      left='50%'
      position='fixed'
      transform='translateX(-50%)'
      // Value found through trial and error. Currently hardcoded so that it
      // sits below the Active Incidents drawer when expanded and on a narrow screen
      zIndex={10}
    >
      <Flex
        align='center'
        bg={bgColor}
        borderColor={borderColor}
        borderRadius='full'
        borderWidth={1}
        boxShadow='lg'
        gap={4}
        px={6}
        py={3}
      >
        <Text color={textLabelColor} fontSize='sm' fontWeight='medium'>
          {label}
        </Text>

        <Divider borderColor={dividerColor} height={4} orientation='vertical' />

        {/* region Secondary button */}
        {secondaryButton && (
          <ActionBarButton
            button={secondaryButton}
            size={buttonSize}
            variant='secondary'
          />
        )}
        {/* endregion */}

        {/* region Extra button */}
        {extraButton && (
          <ActionBarButton
            button={extraButton}
            size={buttonSize}
            variant='secondary'
          />
        )}
        {/* endregion */}

        {/* region Primary button */}
        <ActionBarButton
          button={primaryButton}
          size={buttonSize}
          variant='primary'
        />
        {/* endregion */}
      </Flex>
    </Box>
  )
}

const ActionBarButton = ({
  button,
  size,
  variant,
}: {
  button: ActionBarButtonProps
  size: string
  variant: string
}) => {
  const hasDisabledTooltip = Boolean(
    button.isDisabled && button.disabledTooltipLabel
  )

  return (
    <Tooltip
      hasArrow
      isDisabled={!hasDisabledTooltip}
      label={button.disabledTooltipLabel}
      placement='top'
    >
      <Box
        aria-label={
          hasDisabledTooltip
            ? `${button.text}. ${button.disabledTooltipLabel}`
            : undefined
        }
        as='span'
        display='inline-flex'
        tabIndex={hasDisabledTooltip ? 0 : undefined}
      >
        <Button
          isDisabled={button.isDisabled}
          leftIcon={
            button.icon ? <Icon as={button.icon} boxSize={6} /> : undefined
          }
          onClick={button.onClick}
          size={size}
          variant={variant}
        >
          {button.text}
        </Button>
      </Box>
    </Tooltip>
  )
}

type ActionBarProps = {
  label: string
  primaryButton: ActionBarButtonProps
  extraButton?: ActionBarButtonProps
  secondaryButton?: ActionBarButtonProps
}

type ActionBarButtonProps = {
  disabledTooltipLabel?: string
  icon?: IconType
  isDisabled?: boolean
  text: string
  onClick: () => void
}
